import React, { useEffect, useState } from 'react';
import { History, ArrowDownToLine, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SmartContextCarryBanner — detects when the current encounter is a follow-up
 * for a previously documented patient and offers to carry forward the prior
 * Assessment & Plan as a starting point.
 *
 * Detection heuristics (deterministic, no LLM):
 *   • Reason for visit set to "follow_up" / contains "f/u" or "follow"
 *   • OR notes contains "follow up" / "f/u" / "f /u" pattern in first 200 chars
 *   • AND there exists a prior history entry with same age + gender within
 *     last 90 days (excluding today's encounters and current draft)
 *
 * Surfaces a soft banner above the input — user clicks "Carry forward" to
 * append the prior A/P, "Dismiss" to ignore. Only renders once per encounter.
 */
export const SmartContextCarryBanner = ({
  notes, reasonForVisit, patientAge, patientGender,
  history = [], setNotes, currentDraftId,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [matched, setMatched] = useState(null);

  // Reset on patient change (clearAll dispatches custom events; we listen)
  useEffect(() => {
    const reset = () => { setDismissed(false); setMatched(null); };
    window.addEventListener('writeright:clear-cpts', reset);
    window.addEventListener('writeright:next-patient', reset);
    return () => {
      window.removeEventListener('writeright:clear-cpts', reset);
      window.removeEventListener('writeright:next-patient', reset);
    };
  }, []);

  useEffect(() => {
    if (dismissed) return;
    // Feb 27, 2026 — debounced to 400ms to avoid running the regex +
    // history-filter pipeline on every keystroke (was a source of typing
    // lag on encounters with large history arrays).
    const t = setTimeout(() => {
    // 1. Detect follow-up intent
    const head = (notes || '').slice(0, 250).toLowerCase();
    const reason = (reasonForVisit || '').toLowerCase();
    const isFollowUp =
      reason === 'follow_up' ||
      reason.includes('follow') ||
      /\bf\s*\/\s*u\b/.test(head) ||
      /\bfollow[\s-]?up\b/.test(head);
    if (!isFollowUp) { setMatched(null); return; }
    if (!patientAge || !patientGender) { setMatched(null); return; }

    // 2. Find prior matching history entry (same age + gender, within 90 days, NOT today, NOT current)
    const now = Date.now();
    const ninetyDays = 90 * 24 * 60 * 60 * 1000;
    const todayStr = new Date().toDateString();
    const candidates = (history || []).filter((h) => {
      if (!h?.id || h.id === currentDraftId) return false;
      if (h.is_draft) return false; // only carry from finished/submitted notes
      if (String(h.patient_age) !== String(patientAge)) return false;
      if ((h.patient_gender || '').toLowerCase() !== (patientGender || '').toLowerCase()) return false;
      const ts = new Date(h.timestamp || h.created_at).getTime();
      if (!ts || ts > now || (now - ts) > ninetyDays) return false;
      if (new Date(ts).toDateString() === todayStr) return false; // skip today's
      // Must have an A/P or clinical_note to carry forward
      return Boolean(h.clinical_note || h.assessment_plan);
    });
    if (candidates.length === 0) { setMatched(null); return; }
    // Most recent first
    candidates.sort((a, b) =>
      new Date(b.timestamp || b.created_at).getTime() -
      new Date(a.timestamp || a.created_at).getTime()
    );
    setMatched(candidates[0]);
    }, 400);
    return () => clearTimeout(t);
  }, [notes, reasonForVisit, patientAge, patientGender, history, dismissed, currentDraftId]);

  if (!matched || dismissed) return null;

  const handleCarry = () => {
    // Extract the A/P section from the prior note (or use the whole note as fallback)
    let priorAP = '';
    const fullNote = matched.clinical_note || matched.assessment_plan || '';
    const apMatch = fullNote.match(/(A\/?P\s*Summary|Assessment\s*\/\s*Plan|ASSESSMENT.*PLAN)[\s\S]+/i);
    priorAP = apMatch ? apMatch[0] : fullNote;
    if (!priorAP.trim()) {
      toast.error('No A/P found in prior note');
      return;
    }
    setNotes((prev) => {
      const cur = (prev || '').trim();
      const stamp = new Date(matched.timestamp || matched.created_at).toLocaleDateString();
      const block = `\n\n--- CARRIED FROM PRIOR NOTE (${stamp}) ---\n${priorAP.trim()}\n--- END CARRY FORWARD ---\n`;
      return cur + block;
    });
    toast.success('Prior A/P carried forward — review and edit before generating.');
    setDismissed(true);
  };

  const stamp = new Date(matched.timestamp || matched.created_at).toLocaleDateString();
  const label = matched.patient_label || matched.chief_complaint || 'previous encounter';

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-md border border-sky-500/40 bg-sky-500/10 text-[11px] text-sky-200"
      data-testid="smart-context-carry-banner"
    >
      <History className="w-4 h-4 text-sky-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">Follow-up detected.</span>
        <span className="text-sky-300/80 ml-1.5">
          Prior note for this patient on <span className="font-mono">{stamp}</span>
          {label && label !== 'previous encounter' && (
            <> — <span className="text-sky-200/90 truncate">{label.slice(0, 40)}</span></>
          )}
        </span>
      </div>
      <button
        type="button"
        onClick={handleCarry}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-sky-500/30 text-sky-100 hover:bg-sky-500/50 transition-colors flex-shrink-0"
        data-testid="smart-context-carry-btn"
      >
        <ArrowDownToLine className="w-3 h-3" />
        Carry forward
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-sky-400/60 hover:text-sky-300 transition-colors text-sm leading-none flex-shrink-0"
        title="Dismiss"
        data-testid="smart-context-carry-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default SmartContextCarryBanner;
